import { ListItemCardMobile } from "@components/common/mobile/list-item-card-mobile";
import { SdbTeam, TeamsSortBy } from "@custom-types/teams-types";
import { useTeamMenuOptions } from "@pages/members/teams/use-team-menu-options";
import { useAppSelector } from "@store/store-helper";
import { selectedEntitiesSelector } from "@store/table/table-selector";
import { selectedSdbCompanySelector } from "@store/sdb-company/sdb-company-selector";
import { VirtualizedGrid } from "@components/virtualized/virtualized-grid";
import { Box, Divider, Stack } from "@mui/material";
import { generateTeamDetailsRoute } from "@router/router-helper";
import { FaroTableBulkActions } from "@components/common/faro-table/faro-table-bulk-actions";
import { useTeamsPageBulkActions } from "@pages/members/teams/use-teams-page-bulk-actions";
import { formatUserRoleType, nounPluralize } from "@utils/data-display";
import { useDateTime } from "@hooks/use-date-time";
import { CreateTeam } from "@pages/members/teams/create-team";
import { Text, TeamIcon, NO_TRANSLATE_CLASS } from "@flat-ui";
import { withEllipsis } from "@styles/common-styles";
import { SphereAvatar } from "@components/header/sphere-avatar";
import { teamDisplayProperties } from "@components/table/teams/teams-table-utils";
import { useMemo } from "react";
import { SkeletonMemberListItemCard } from "@components/common/mobile/skeleton-member-list-item-card";

interface Props {
  /** The teams that are shown in the table */
  teams: SdbTeam[];

  /** Whether the data for showing a table is loading or not */
  isLoading: boolean;

  /** Teams sort criteria */
  sortBy: TeamsSortBy;
}

/**
 * Mobile-specific view for teams list with action bar and card layout.
 */
export function TeamsMobileView({
  teams,
  isLoading,
  sortBy,
}: Props): JSX.Element | null {
  const menuItems = useTeamMenuOptions();

  const selectedEntities = useAppSelector(selectedEntitiesSelector("teams"));
  const selectedSdbCompany = useAppSelector(selectedSdbCompanySelector);
  const { formatDate } = useDateTime();

  const isAnyRowSelected = selectedEntities.length !== 0;

  const allTeamIds = useMemo(() => teams.map((team) => team.id), [teams]);

  // The available bulk actions to display according to the table context.
  const bulkActionsButtons = useTeamsPageBulkActions({
    companyId: selectedSdbCompany?.id ?? "",
    teams,
  });

  if (!selectedSdbCompany) {
    return null;
  }

  return (
    <VirtualizedGrid
      key={sortBy}
      items={teams}
      isLoading={isLoading}
      renderSkeletonItem={(index) => (
        <Box key={index}>
          <SkeletonMemberListItemCard />
        </Box>
      )}
      estimateSize={80}
      renderItem={(team, index) => {
        const creationDate = formatDate(team.createdAt);
        const role = formatUserRoleType(team.role);
        const membersCount = nounPluralize({
          counter: team.memberCount,
          word: "member",
        });
        const secondaryText = `${role} • ${membersCount}`;

        return (
          <Box key={team.id}>
            {index !== 0 && <Divider />}

            <ListItemCardMobile<SdbTeam>
              item={team}
              image={
                <Stack justifyContent="center">
                  <SphereAvatar
                    icon={<TeamIcon sx={{ width: "20px", height: "20px" }} />}
                    size="small"
                    shouldHideWhiteRim={true}
                    backgroundColor={
                      teamDisplayProperties[team.category.kind].color
                    }
                  />
                </Stack>
              }
              title={
                <Text
                  variant="heading14"
                  sx={{ ...withEllipsis }}
                  className={NO_TRANSLATE_CLASS}
                >
                  {team.name}
                </Text>
              }
              href={generateTeamDetailsRoute(selectedSdbCompany.id, team.id)}
              subtitle={`Created: ${creationDate}`}
              secondaryText={secondaryText}
              menuItems={menuItems}
            />
          </Box>
        );
      }}
      shouldRenderFooterOutside={isAnyRowSelected}
      renderFooterAction={() =>
        isAnyRowSelected ? (
          <FaroTableBulkActions
            bulkActionsButtons={bulkActionsButtons}
            numberOfSelectedRows={selectedEntities.length}
            isLoading={isLoading}
            allRowIds={allTeamIds}
          />
        ) : (
          <CreateTeam />
        )
      }
    />
  );
}
